import { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import RankingsTable, { PAGE_SIZE } from '../components/RankingsTable';
import usePrerenderReady from '../hooks/usePrerenderReady';
import { SITE_URL } from '../seoConfig';
import { buildItemListSchema, toJsonLd } from '../schema';
import PositionFilter from '../components/PositionFilter';
import PlayerSearch from '../components/PlayerSearch';
import ExportButton from '../components/ExportButton';
import CsvUploadDialog from '../components/CsvUploadDialog';
import RosterRxIntro from '../components/RosterRxIntro';
import {
  getRankings,
  getAggregatedRankings,
  getPersonalAggregatedRankings,
  getSources,
  getUserSources,
  getUserRankings,
  deleteUserSource,
  uploadRankings,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const FALLBACK_SOURCES = ['ESPN', 'Yahoo', 'FantasyPros'];
const ABOUT_COLLAPSED_KEY = 'fa_rankings_about_collapsed';

function readStoredAboutCollapsed() {
  return localStorage.getItem(ABOUT_COLLAPSED_KEY) === 'true';
}

function formatRelativeTime(epochMs) {
  if (!epochMs) return null;
  const diffSec = Math.floor((Date.now() - epochMs) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)} days ago`;
}

function RankingsView() {
  const { isLoggedIn } = useAuth();
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('consensus');
  const [posFilter, setPosFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [scoringType, setScoringType] = useState('ppr');
  const [sources, setSources] = useState([]);
  const [userSources, setUserSources] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [pageInfo, setPageInfo] = useState({ page: 1, pageCount: 1 });
  const [aboutCollapsed, setAboutCollapsed] = useState(readStoredAboutCollapsed);

  useEffect(() => {
    localStorage.setItem(ABOUT_COLLAPSED_KEY, String(aboutCollapsed));
  }, [aboutCollapsed]);

  const handlePageInfoChange = useCallback((info) => setPageInfo(info), []);

  const fetchSources = useCallback(async () => {
    try {
      const data = await getSources();
      setSources(data);
    } catch {
      setSources(FALLBACK_SOURCES.map(name => ({ name })));
    }
  }, []);

  const fetchUserSources = useCallback(async () => {
    if (!isLoggedIn) { setUserSources([]); return; }
    try {
      const data = await getUserSources();
      setUserSources(data);
    } catch {
      setUserSources([]);
    }
  }, [isLoggedIn]);

  useEffect(() => { fetchSources(); }, [fetchSources]);
  useEffect(() => { fetchUserSources(); }, [fetchUserSources]);

  const userSourceNames = useMemo(() => new Set(userSources.map(s => s.name)), [userSources]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (viewMode === 'consensus') {
        const data = isLoggedIn
          ? await getPersonalAggregatedRankings(scoringType)
          : await getAggregatedRankings(scoringType);
        setRankings(data);
      } else if (userSourceNames.has(viewMode)) {
        const data = await getUserRankings(viewMode, scoringType);
        setRankings(data);
      } else {
        const data = await getRankings(viewMode, scoringType);
        setRankings(data);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load rankings');
    } finally {
      setLoading(false);
    }
  }, [viewMode, scoringType, isLoggedIn, userSourceNames]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    let result = rankings;
    if (posFilter !== 'ALL') {
      result = result.filter(r => r.position === posFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(r =>
        r.playerName.toLowerCase().includes(q) ||
        (r.team && r.team.toLowerCase().includes(q))
      );
    }
    return result;
  }, [rankings, posFilter, search]);

  const isAggregated = viewMode === 'consensus';

  // R5.4: built from the same `filtered` list (default sort, unfiltered on first load) the table
  // itself renders, so the schema can never assert a different player set than the visible page.
  const itemListSchema = useMemo(
    () => buildItemListSchema({ items: filtered, path: '/' }),
    [filtered]
  );

  async function handleUpload(sourceName, csvContent, scoringFormat) {
    await uploadRankings(sourceName, csvContent, scoringFormat);
    await fetchSources();
    await fetchUserSources();
    if (viewMode === 'consensus') fetchData();
  }

  async function handleDeleteUserSource(name) {
    try {
      await deleteUserSource(name);
    } catch {
      // ignore — pill still removed below
    }
    setUserSources(prev => prev.filter(s => s.name !== name));
    if (viewMode === name) {
      setViewMode('consensus');
    }
    fetchData();
  }

  usePrerenderReady(!loading);

  return (
    <div>
      <Helmet>
        <title>RosterRx — Fantasy Football Rankings & Draft Assistant</title>
        <meta
          name='description'
          content='Consensus fantasy football rankings aggregated from ESPN, Yahoo, and FantasyPros, plus an AI-powered live draft assistant. Free rankings aggregation, Pro draft tools starting at $5/month.'
        />
        <link rel='canonical' href={SITE_URL} />
        <meta property='og:title' content='RosterRx — Fantasy Football Rankings & Draft Assistant' />
        <meta
          property='og:description'
          content='Consensus fantasy football rankings aggregated from ESPN, Yahoo, and FantasyPros, plus an AI-powered live draft assistant.'
        />
        <meta property='og:url' content={SITE_URL} />
        <meta property='og:type' content='website' />
        <meta name='twitter:card' content='summary' />
        <meta name='twitter:title' content='RosterRx — Fantasy Football Rankings & Draft Assistant' />
        <meta
          name='twitter:description'
          content='Consensus fantasy football rankings aggregated from ESPN, Yahoo, and FantasyPros, plus an AI-powered live draft assistant.'
        />
        <script type='application/ld+json'>{toJsonLd(itemListSchema)}</script>
      </Helmet>
      {aboutCollapsed ? (
        <button
          type='button'
          className='btn btn-secondary rankings-about-toggle'
          onClick={() => setAboutCollapsed(false)}
        >
          About RosterRx
        </button>
      ) : (
        <div className='card rankings-about'>
          <div className='rankings-about-body'>
            <RosterRxIntro />
          </div>
          <button
            type='button'
            className='btn btn-secondary rankings-about-minimize'
            onClick={() => setAboutCollapsed(true)}
            aria-label='Minimize'
            title='Minimize'
          >
            _
          </button>
        </div>
      )}
      <div className='toolbar'>
        <div className='filter-group'>
          <button
            className={`filter-pill ${scoringType === 'standard' ? 'active' : ''}`}
            onClick={() => setScoringType('standard')}
          >
            Standard
          </button>
          <button
            className={`filter-pill ${scoringType === 'ppr' ? 'active' : ''}`}
            onClick={() => setScoringType('ppr')}
          >
            PPR
          </button>
        </div>
      </div>
      <div className='toolbar'>
        <div className='filter-group'>
          <button
            className={`filter-pill ${viewMode === 'consensus' ? 'active' : ''}`}
            onClick={() => { setRankings([]); setViewMode('consensus'); }}
          >
            Consensus
          </button>
          {sources.map(source => {
            const relTime = formatRelativeTime(source.lastFetchedAt || source.lastUpdated);
            const yahooNoPprNote = source.name === 'Yahoo' && scoringType === 'ppr'
              ? '\nYahoo does not publish a separate PPR ranking — same as Standard.'
              : '';
            const tooltipText = source.sourceUrl
              ? `${source.sourceUrl}\n${new Date(source.lastFetchedAt || source.lastUpdated || 0).toLocaleString()}${yahooNoPprNote}`
              : (yahooNoPprNote.trim() || undefined);
            return (
              <span key={source.name} className='source-pill-wrapper'>
                <button
                  className={`filter-pill ${viewMode === source.name ? 'active' : ''}`}
                  onClick={() => { setRankings([]); setViewMode(source.name); }}
                  title={tooltipText}
                >
                  {source.name}
                  {relTime && <span className='source-pill-time'> · {relTime}</span>}
                </button>
                {source.sourceUrl && (
                  <a
                    href={source.sourceUrl}
                    target='_blank'
                    rel='noreferrer'
                    className='source-pill-link'
                    title={tooltipText}
                  >
                    ↗
                  </a>
                )}
              </span>
            );
          })}
          {isLoggedIn && userSources.map(source => (
            <span key={source.name} className='source-pill-wrapper'>
              <button
                className={`filter-pill ${viewMode === source.name ? 'active' : ''}`}
                onClick={() => { setRankings([]); setViewMode(source.name); }}
              >
                {source.name}
              </button>
              <button
                className='source-pill-delete'
                title={`Remove ${source.name}`}
                onClick={() => handleDeleteUserSource(source.name)}
              >
                ×
              </button>
            </span>
          ))}
          <button
            className='filter-pill'
            onClick={() => setShowUpload(true)}
            title={isLoggedIn ? 'Upload CSV rankings' : 'Sign in to upload CSV rankings'}
            disabled={!isLoggedIn}
          >
            +
          </button>
        </div>
        <PositionFilter selected={posFilter} onChange={setPosFilter} />
        <PlayerSearch value={search} onChange={setSearch} />
        {/* Intentionally reads the full `filtered` list, not the paginated slice inside
            RankingsTable -- CSV export must always include every filtered player. */}
        <ExportButton rankings={filtered} aggregated={isAggregated} />
        <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          {pageInfo.pageCount > 1
            ? `Showing ${(pageInfo.page - 1) * PAGE_SIZE + 1}-${Math.min(pageInfo.page * PAGE_SIZE, filtered.length)} of ${filtered.length} players`
            : `${filtered.length} players`}
        </span>
      </div>

      {error && <div className='error-banner'>{error}</div>}

      {loading ? (
        <div className='loading'>
          <div className='spinner' />
          <p>Loading rankings...</p>
        </div>
      ) : (
        <div className='card'>
          <RankingsTable
            rankings={filtered}
            aggregated={isAggregated}
            posFilter={posFilter}
            onPageInfoChange={handlePageInfoChange}
          />
        </div>
      )}

      {showUpload && (
        <CsvUploadDialog
          onClose={() => setShowUpload(false)}
          onUpload={handleUpload}
        />
      )}
    </div>
  );
}

export default RankingsView;
